import { ShortenedWarranty } from './../../models/shortened-warranty.model';
import { ConfirmDialogComponent } from './../../dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { GuarantiesService } from './../../services/guaranties.service';
import { ErrorResponse } from './../../models/error.model';
import { PageEvent } from '@angular/material/paginator';
import { UtilsService } from 'src/app/services/utils.service';
import { DataTableColumn, PaginatorStatus, SortConfiguration } from './../generics/data-table/data-table.component';
import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { CustomTableFilter } from '../generics/table-filter/table-filter.component';
import { UserType } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import { BrokersService } from '../../services/brokers.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-guaranties',
  templateUrl: './guaranties.component.html',
  styleUrls: ['./guaranties.component.scss']
})
export class GuarantiesComponent implements OnInit {

    @ViewChild('buttonsColumnTemplate', { static: true })
    buttonsColumnTemplate!: TemplateRef<any>;

    paginatorStatus: PaginatorStatus = {
        itemsPerPage: 50,
        page: 0,
        totalElementCount: 0
    };

    columns: DataTableColumn[] = [
        { name: 'ID', prop: 'id', sortable: true },
        { name: 'Alquiler', prop: 'cotizacionAlquiler', type: 'money' },
        { name: 'Expensas', prop: 'cotizacionExpensas', type: 'money' },
        { name: 'Agente', parseValue: (value: ShortenedWarranty) => value.agenteNombre ? value.agenteNombre + ' ' + value.agenteApellido : 'Sin asignar', minWidth: '100px' },
        { name: 'E-Mail', prop: 'emailInquilinoPrincipal', type: 'email' },
        { name: 'Estado', prop: 'estado', sortable: true, type: 'text' },
        { name: 'Propiedad', prop: 'propiedadDireccion', type: 'resumed-address', minWidth: '160px' },
        { name: '', width: '130px', minWidth: '130px' }
    ];

    sortConfiguration?: SortConfiguration;

    guaranties!: ShortenedWarranty[];

    filters: CustomTableFilter[] = [];
    filterValues: any = {};

    userType?: UserType;

    constructor(
        private guarantiesService: GuarantiesService,
        private authService: AuthService,
        private brokersService: BrokersService,
        private dialog: MatDialog,
        private utilsService: UtilsService
    ) {}

    ngOnInit(): void {
        this.columns.find(col => col.name === '')!.template = this.buttonsColumnTemplate;
        this.filters = this.buildFilters();
        this.getGuaranties();
        this.userType = this.authService.getUser()?.tipo;
        if (this.userType === 'CORPORATIVO') {
            this.columns[this.columns.length - 1].width = '163px';
            this.columns[this.columns.length - 1].minWidth = '163px';
        }
    }

    private buildFilters(): CustomTableFilter[] {
        return [
            { key: 'id', description: 'ID', type: 'NUMBER' },
            { key: 'estado', description: 'Estado', type: 'ENUM', availableOptions: [
                { codigo: 'DOCUMENTACION', descripcion: 'DOCUMENTACION' },
                { codigo: 'PAGO_PENDIENTE', descripcion: 'PAGO PENDIENTE' },
                { codigo: 'GENERAR_CONTRATO', descripcion: 'GENERAR CONTRATO' },
                { codigo: 'AUTORIZACION_PENDIENTE', descripcion: 'AUTORIZACION PENDIENTE' },
                { codigo: 'FIRMA_DIGITAL', descripcion: 'FIRMA DIGITAL' },
                { codigo: 'FIRMA_PENDIENTE', descripcion: 'FIRMA PENDIENTE' },
                { codigo: 'ACTIVA', descripcion: 'ACTIVA' },
                { codigo: 'FINALIZADA', descripcion: 'FINALIZADA' },
                { codigo: 'BAJA_ANTICIPADA', descripcion: 'BAJA ANTICIPADA' },
                { codigo: 'BAJA_POR_ADMIN', descripcion: 'BAJA POR ADMIN' },
                { codigo: 'BAJA_POR_AGENTE', descripcion: 'BAJA POR AGENTE' },
            ]},
            { key: 'id-agente', description: 'Agente', type: 'AUTOCOMPLETE',
              searchFn: (query: string) => forkJoin([
                  this.brokersService.getBrokers(0, 20, [], { nombre: query }),
                  this.brokersService.getBrokers(0, 20, [], { apellido: query })
              ]).pipe(map(([r1, r2]) => {
                  const all = [...(r1.body ?? []), ...(r2.body ?? [])];
                  const unique = all.filter((b, i, arr) => arr.findIndex(x => x.id === b.id) === i);
                  return unique.map(b => ({ codigo: String(b.id), descripcion: `${b.nombre} ${b.apellido}` }));
              }))
            },
            { key: 'sin-agente', description: 'Agente asignado', type: 'ENUM', availableOptions: [
                { codigo: 'false', descripcion: 'Con agente' },
                { codigo: 'true', descripcion: 'Sin agente' },
            ]},
            { key: 'propiedad-codigo', description: 'Código de propiedad', type: 'STRING' },
            { key: 'propiedad-direccion', description: 'Dirección', type: 'STRING' },
            { key: 'inquilino-nombre', description: 'Nombre del inquilino', type: 'STRING' },
            { key: 'inquilino-apellido', description: 'Apellido del inquilino', type: 'STRING' },
            { key: 'inquilino-dni', description: 'DNI del inquilino', type: 'DIGITS' },
            { key: 'propietario-nombre', description: 'Nombre del propietario', type: 'STRING' },
            { key: 'propietario-apellido', description: 'Apellido del propietario', type: 'STRING' },
            { key: 'propietario-dni', description: 'DNI del propietario', type: 'DIGITS' },
            { key: 'fecha-desde', description: 'Fecha desde', type: 'DATE' },
            { key: 'fecha-hasta', description: 'Fecha hasta', type: 'DATE' },
            { key: 'alquiler-desde', description: 'Monto desde $', type: 'NUMBER' },
            { key: 'alquiler-hasta', description: 'Monto hasta $', type: 'NUMBER' },
        ];
    }

    getGuaranties() {
        this.guarantiesService.getGuarantees(this.paginatorStatus.page, this.paginatorStatus.itemsPerPage, this.sortConfiguration ? [this.sortConfiguration] : [], this.filterValues)
        .subscribe(response => {
            this.paginatorStatus.totalElementCount = parseInt(response.headers.get('pagination-total-count') ?? '0', 10);
            this.guaranties = response.body ?? [];
        }, (error: ErrorResponse) => this.utilsService.alert('Error al obtener garantías', error.error?.descripcion ?? ''));
    }

    updateGuaranties(event: PageEvent) {
        this.paginatorStatus.itemsPerPage = event.pageSize;
        this.paginatorStatus.page = event.pageIndex;
        this.getGuaranties();
    }

    updateSort(event: SortConfiguration) {
        this.sortConfiguration = event;
        this.getGuaranties();
    }

    deleteWarranty(warranty: ShortenedWarranty) {
        const dialog = this.dialog.open(ConfirmDialogComponent, {
            data: {
                titulo: '¿Dar de baja la garantía?',
                descripcion: 'Esta acción no se puede deshacer.',
                input: 'Motivo',
                botonCancelar: 'Cancelar',
                botonAceptar: 'Confirmar'
            }
        });
        dialog.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined && result !== null) {
                this.guarantiesService.deleteWarranty(warranty.id).subscribe(_ => {
                    this.utilsService.alert('Garantía dada de baja correctamente', `Se ha dado de baja la garantía número ${warranty.id} de la plataforma.`);
                    this.getGuaranties();
                }, (error: ErrorResponse) => this.utilsService.alert('Error al borrar garantía', error.error?.descripcion ?? ''));
            }
        });
    }

    setFilterValues(values: any) {
        this.filterValues = values;
        this.getGuaranties();
    }
}
